import { CANDIDATO_TOKEN_KEY, LOGO_DEFENSORIA_SRC } from './candidato.constants';
import { CandidatoAuthService } from './candidato-auth.service';
import { IoPencil } from "react-icons/io5";
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface ProcessoSeletivoCandidato {
  id: number;
  processoSeletivoId: number;
  processoSeletivoNome: string;
  dataInscricao: string;
  situacao: string;
  statusProcesso: string;
  dataInicioInscricoes: string;
  dataFimInscricoes: string;
}

export default function MeusProcessosPage() {
  const [processos, setProcessos] = useState<ProcessoSeletivoCandidato[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const candidatoAuthService = new CandidatoAuthService();

  useEffect(() => {
    // Verificar token na URL ou localStorage
    let token = searchParams.get('token') || localStorage.getItem(CANDIDATO_TOKEN_KEY);

    if (!token) {
      toast.error('Acesso não autorizado. Faça login novamente.');
      navigate('/candidato/login');
      return;
    }

    // Validar token
    candidatoAuthService.validarToken(token)
      .then((result) => {
        if (!result.valid) {
          localStorage.removeItem(CANDIDATO_TOKEN_KEY);
          toast.error('Token inválido ou expirado. Faça login novamente.');
          navigate('/candidato/login');
          return;
        }

        // Salvar token se veio da URL
        if (searchParams.get('token')) {
          localStorage.setItem(CANDIDATO_TOKEN_KEY, token!);
        }

        // Carregar processos
        carregarProcessos(token!);
      })
      .catch(() => {
        localStorage.removeItem(CANDIDATO_TOKEN_KEY);
        toast.error('Erro ao validar acesso. Faça login novamente.');
        navigate('/candidato/login');
      });
  }, [searchParams, navigate]);

  const carregarProcessos = async (token: string) => {
    setIsLoading(true);
    try {
      const dados = await candidatoAuthService.listarMeusProcessos(token);
      setProcessos(dados);
    } catch (error: any) {
      const errorMessage = error?.message || 'Erro ao carregar processos seletivos.';
      toast.error(errorMessage);

      // Se o erro for de token inválido, redirecionar para login
      if (errorMessage.includes('Token inválido') || errorMessage.includes('expirado')) {
        localStorage.removeItem(CANDIDATO_TOKEN_KEY);
        navigate('/candidato/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatarData = (data: string) => {
    if (!data) return '-';
    const date = new Date(data);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatarDataHora = (data: string) => {
    if (!data) return '-';
    const date = new Date(data);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (situacao: string) => {
    switch (situacao?.toLowerCase()) {
      case 'inscricao_realizada':
        return 'bg-primary';
      case 'inscricao_aprovada':
        return 'bg-success';
      case 'inscricao_rejeitada':
        return 'bg-danger';
      case 'inscricao_cancelada':
        return 'bg-secondary';
      default:
        return 'bg-info';
    }
  };

  const getStatusLabel = (situacao: string) => {
    switch (situacao?.toLowerCase()) {
      case 'inscricao_realizada':
        return 'Inscrição Realizada';
      case 'inscricao_aprovada':
        return 'Inscrição Aprovada';
      case 'inscricao_rejeitada':
        return 'Inscrição Rejeitada';
      case 'inscricao_cancelada':
        return 'Inscrição Cancelada';
      default:
        return situacao || 'Desconhecido';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(CANDIDATO_TOKEN_KEY);
    navigate('/candidato/login');
  };

  if (isLoading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4 mb-5">
      <div className="card-body text-center p-1 mb-5">
        <img style={{ width: '150px', height: '150px' }} src={LOGO_DEFENSORIA_SRC} alt="Logo Defensoria" className="img-fluid" />
      </div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">Meus Processos Seletivos</h2>
        <button
          className="btn btn-outline-secondary d-flex align-items-center"
          onClick={handleLogout}
          style={{ borderRadius: '8px' }}
        >
          <i className="bi bi-box-arrow-right me-2"></i>
          Sair
        </button>
      </div>

      {processos.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center p-5">
            <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
            <p className="text-muted mt-3 mb-0">
              Você ainda não possui inscrições em processos seletivos.
            </p>
          </div>
        </div>
      ) : (
        <div className={`row ${processos.length === 1 ? 'justify-content-center' : ''}`}>
          {processos.map((processo) => (
            <div
              key={processo.id}
              className={processos.length === 1 ? 'col-md-8 col-lg-6 mb-4' : 'col-md-6 mb-4'}
            >
              <div className="card h-100 position-relative" style={{
                borderRadius: '12px',
                border: '2px solid #dee2e6',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)'
              }}>
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title flex-grow-1 mb-0 fw-bold" style={{ fontSize: '1.25rem' }}>
                      {processo.processoSeletivoNome}
                    </h5>
                    <button
                      className="btn d-flex align-items-center justify-content-center"
                      onClick={() =>
                        navigate(`/candidato/inscricao/${processo.processoSeletivoId}`, {
                          state: { candidatoId: processo.id },
                        })
                      }
                      title="Editar inscrição"
                      style={{
                        width: '40px',
                        height: '40px',
                        padding: '0',
                        borderRadius: '10px',
                        border: '2px solid #0d6efd',
                        backgroundColor: 'transparent',
                        color: '#0d6efd',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0d6efd';
                        e.currentTarget.style.borderColor = '#0d6efd';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
                        const icon = e.currentTarget.querySelector('svg');
                        if (icon) icon.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#0d6efd';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        const icon = e.currentTarget.querySelector('svg');
                        if (icon) icon.style.color = '#0d6efd';
                      }}
                    >
                      <IoPencil style={{
                        fontSize: '1rem',
                        transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        color: '#0d6efd'
                      }} />
                    </button>
                  </div>

                  <div className="mb-3">
                    <span className={`badge ${getStatusBadgeClass(processo.situacao)} me-2 px-3 py-2`} style={{ fontSize: '0.85rem' }}>
                      {getStatusLabel(processo.situacao)}
                    </span>
                    <span className="badge bg-secondary px-3 py-2" style={{ fontSize: '0.85rem' }}>
                      {processo.statusProcesso}
                    </span>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-calendar-check me-2 text-muted"></i>
                      <small className="text-muted fw-semibold">Data da Inscrição:</small>
                    </div>
                    <div className="ms-4">{formatarDataHora(processo.dataInscricao)}</div>
                  </div>

                  <div className="mb-0">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-calendar-range me-2 text-muted"></i>
                      <small className="text-muted fw-semibold">Período de Inscrições:</small>
                    </div>
                    <div className="ms-4">
                      {formatarData(processo.dataInicioInscricoes)} até{' '}
                      {formatarData(processo.dataFimInscricoes)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

